<template>
  <div class="portal-apps">
    <div class="portal-bg" aria-hidden="true">
      <div class="portal-wash"></div>
      <div class="portal-grid"></div>
    </div>

    <header class="portal-top">
      <div class="portal-brand">
        <CcmisLogo variant="mark" theme="dark" />
        <div>
          <p class="portal-kicker">UGNAY</p>
          <h1>Choose a system</h1>
        </div>
      </div>
      <div class="portal-user">
        <span>{{ userLabel }}</span>
        <button type="button" class="portal-link" @click="goHome">
          Skip to CCMIS dashboard
        </button>
      </div>
    </header>

    <main class="portal-main">
      <p class="portal-sub">
        Sign-in once. Open only the systems you’re allowed to use.
        More CHEDRO apps can be added here later.
      </p>

      <div v-if="loading" class="portal-state">
        <div class="spinner-border spinner-border-sm mr-2"></div>
        Loading your applications…
      </div>

      <div v-else-if="error" class="portal-state portal-state--error">
        {{ error }}
        <button type="button" class="btn btn-sm btn-light ml-2" @click="loadApps">
          Retry
        </button>
      </div>

      <div v-else-if="!apps.length" class="portal-state">
        No applications are assigned to your account yet. Contact an administrator.
      </div>

      <div v-else class="portal-grid-apps">
        <button
          v-for="app in apps"
          :key="app.slug"
          type="button"
          class="app-tile"
          :style="{ '--accent': app.accent_color || '#15263f' }"
          :disabled="launching === app.slug"
          @click="launch(app)"
        >
          <span class="app-icon" aria-hidden="true">
            <i class="bi" :class="app.icon || 'bi-box'"></i>
          </span>
          <span class="app-copy">
            <span class="app-name">{{ app.name }}</span>
            <span class="app-tag">{{ app.tagline }}</span>
          </span>
          <span class="app-go">
            <span v-if="launching === app.slug" class="spinner-border spinner-border-sm"></span>
            <i v-else class="bi bi-arrow-right"></i>
          </span>
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import CcmisLogo from "@/components/brand/CcmisLogo.vue";

export default {
  name: "PortalApps",
  components: { CcmisLogo },
  data() {
    return {
      apps: [],
      loading: true,
      error: null,
      launching: null,
    };
  },
  computed: {
    userLabel() {
      const u = this.$store.state.user || {};
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
      return name || u.email || "Signed in";
    },
  },
  mounted() {
    document.title = "Applications | UGNAY";
    this.loadApps();
  },
  methods: {
    async loadApps() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get("/api/portal/my-apps");
        this.apps = data.apps || [];
        // Single-app auto-launch keeps UX snappy for most staff.
        if (this.apps.length === 1) {
          await this.launch(this.apps[0], true);
        }
      } catch (e) {
        this.error = "Could not load your applications.";
      } finally {
        this.loading = false;
      }
    },
    goHome() {
      this.$router.push({ path: "/dashboard" });
    },
    legacyCcmisRoute() {
      const role = (this.$store.state.user && this.$store.state.user.role) || "";
      if (role === "Registrar") return { name: "RedirectToEcav" };
      if (role === "Records") return { name: "RecordsDashboard" };
      if (role === "NstpCoordinator") return { name: "NstpClient" };
      return { path: "/dashboard" };
    },
    async launch(app, auto = false) {
      if (!app || this.launching) return;
      this.launching = app.slug;

      try {
        if (app.slug === "ccmis") {
          this.$router.push(this.legacyCcmisRoute());
          return;
        }

        const { data } = await axios.post("/api/portal/handoff", { slug: app.slug });
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
          return;
        }
        throw new Error("Missing redirect URL");
      } catch (e) {
        if (!auto) {
          toastr.error(
            (e.response && e.response.data && e.response.data.message) ||
              "Unable to open this application.",
            "Launch failed"
          );
        } else {
          // Auto-launch failed — stay on chooser.
          this.error = "Could not auto-open your application.";
        }
      } finally {
        this.launching = null;
      }
    },
  },
};
</script>

<style scoped>
.portal-apps {
  position: relative;
  min-height: 100vh;
  color: #f4f7fb;
  background: #0b1524;
  overflow: hidden;
}

.portal-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.portal-wash {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 20% 0%, rgba(29, 78, 216, 0.35), transparent 60%),
    radial-gradient(ellipse 50% 40% at 90% 20%, rgba(232, 164, 58, 0.18), transparent 55%),
    linear-gradient(180deg, #0f1d36 0%, #0b1524 100%);
}

.portal-grid {
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, #000 30%, transparent 75%);
}

.portal-top,
.portal-main {
  position: relative;
  z-index: 1;
  max-width: 920px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem;
}

.portal-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 2rem;
}

.portal-brand {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.portal-kicker {
  margin: 0;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  font-weight: 700;
  color: #e8a43a;
}

.portal-brand h1 {
  margin: 0.1rem 0 0;
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.portal-user {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  font-size: 0.88rem;
  color: rgba(244, 247, 251, 0.75);
}

.portal-link {
  border: 0;
  background: transparent;
  color: #7eb8e8;
  font-size: 0.8rem;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.portal-sub {
  margin: 0.5rem 0 1.5rem;
  max-width: 36rem;
  color: rgba(244, 247, 251, 0.72);
  line-height: 1.55;
}

.portal-state {
  padding: 1.25rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.portal-state--error {
  color: #fecaca;
}

.portal-grid-apps {
  display: grid;
  gap: 0.9rem;
}

@media (min-width: 720px) {
  .portal-grid-apps {
    grid-template-columns: 1fr 1fr;
  }
}

.app-tile {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  text-align: left;
  padding: 1.1rem 1rem;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.app-tile:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--accent) 55%, white);
  background: rgba(255, 255, 255, 0.09);
}

.app-tile:disabled {
  opacity: 0.7;
  cursor: wait;
}

.app-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--accent) 85%, black);
  font-size: 1.35rem;
  flex-shrink: 0;
}

.app-copy {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
}

.app-name {
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: 0.02em;
}

.app-tag {
  font-size: 0.82rem;
  color: rgba(244, 247, 251, 0.65);
  line-height: 1.35;
}

.app-go {
  color: rgba(244, 247, 251, 0.7);
  font-size: 1.15rem;
}
</style>
